import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { formatNaira } from "../utils/money";
import { lagosParts } from "../utils/tripLog";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PaymentAndEarningCard = ({ analytics = {} }) => {
  // Debug log to see what data we're receiving
  console.log('PaymentChart - Received analytics:', analytics);

  // Process analytics data for the chart
  const chartData = analytics.revenueByMonth?.length > 0 
    ? analytics.revenueByMonth.map(item => {
        // Log each item for debugging
        console.log('Processing month data:', item);
        return {
          name: monthNames[item.month - 1],
          fare: item.totalFareNgn || 0,
          commission: item.totalCommissionNgn || 0,
          withdrawal: 0, // Will be populated with monthly data if available
          month: item.month,
          year: item.year || lagosParts().year // Default to current year if not provided
        };
      }).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      })
    : [];

  // Add withdrawal data to chart if available
  if (analytics.withdrawals?.allWithdrawals?.length > 0) {
    // Group withdrawals by month
    const withdrawalsByMonth = analytics.withdrawals.allWithdrawals.reduce((acc, w) => {
      if (w.status === 'success') { // Only include successful withdrawals
        const { month, year } = lagosParts(w.createdAt) || {};
        const key = `${year}-${month}`;
        const amount = (parseInt(w.amountKobo) || 0) / 100; // Convert from kobo to Naira
        
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += amount;
      }
      return acc;
    }, {});

    // Add withdrawals to chart data
    chartData.forEach(item => {
      const key = `${item.year}-${item.month}`;
      if (withdrawalsByMonth[key]) {
        item.withdrawal = withdrawalsByMonth[key];
      }
    });

    // Debug log
    console.log('Withdrawals by month:', withdrawalsByMonth);
  }

  // Debug log the processed chart data
  console.log('Processed chart data:', chartData);

  // Get totals from analytics or calculate from chart data
  // Missing revenue with no monthly rows is unknown (null), not ₦0. A real 0 stays 0.
  const totalOr = (value, key) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (chartData.length === 0) return null;
    return chartData.reduce((sum, item) => sum + (item[key] || 0), 0);
  };
  const totalFare = totalOr(analytics.revenue?.totalFareNgn, 'fare');
  const totalCommission = totalOr(analytics.revenue?.totalCommissionNgn, 'commission');
  
  // All successful payouts, from the server total. Unknown shows "—", never ₦0.
  const totalWithdrawal = analytics.withdrawals?.totalAmount ?? null;
  
  // Debug log the withdrawal data
  console.log('Withdrawal summary in chart:', {
    totalFromAnalytics: analytics.withdrawals?.totalAmount,
    allWithdrawals: analytics.withdrawals?.allWithdrawals,
    calculatedTotal: totalWithdrawal
  });
  
  // Debug log the totals
  console.log('Total Fare:', totalFare, 'Total Commission:', totalCommission);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null) return '—';
    if (amount >= 1000) {
      return `₦${(amount / 1000).toFixed(1)}K`;
    }
    return `₦${amount}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-blue-600 text-base font-medium mb-1">
          Payment and Earning
        </h3>
        <p className="text-gray-500 text-sm mb-2">
          {chartData.length > 0 
            ? `${monthNames[chartData[0].month - 1]} - ${monthNames[chartData[chartData.length - 1].month - 1]} ${chartData[0].year}`
            : 'No data available'}
        </p>
        <div className="flex items-center gap-6">
          <div>
            <p className="text-sm text-gray-500">Total Fare</p>
            <h2 className="text-2xl font-bold text-gray-900">
              {formatCurrency(totalFare)}
            </h2>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Commission</p>
            <h2 className="text-2xl font-bold text-gray-900">
              {formatCurrency(totalCommission)}
            </h2>
          </div>
        </div>
      </div>

      {/* Chart and Metrics */}
      <div className="flex gap-8">
        {/* Chart */}
        <div className="flex-1" style={{ height: '240px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke="#f0f0f0" 
              />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 12, fill: '#9CA3AF' }}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={false}
                width={0}
              />
              <Tooltip 
                cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                formatter={(value, name) => {
                  if (name === 'Withdrawals') {
                    return `₦${value.toLocaleString()}`;
                  }
                  return `₦${value.toLocaleString()}`;
                }}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Bar 
                name="Fare"
                dataKey="fare" 
                fill="#3B82F6" 
                radius={[4, 4, 0, 0]} 
              />
              <Bar 
                name="Commission"
                dataKey="commission" 
                fill="#F59E0B" 
                radius={[4, 4, 0, 0]} 
              />
              <Bar 
                name="Withdrawals"
                dataKey="withdrawal" 
                fill="#10B981" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Metrics */}
        <div className="flex flex-col justify-center space-y-6">
          {/* Total Fare */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-gray-500 text-xs uppercase tracking-wide">TOTAL FARE</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalFare)}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {typeof analytics.revenue?.totalRides === 'number'
                ? `${analytics.revenue.totalRides.toLocaleString()} rides`
                : '—'}
            </div>
          </div>

          {/* Total Commission */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span className="text-gray-500 text-xs uppercase tracking-wide">COMMISSION</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalCommission)}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {totalFare > 0 && totalCommission !== null
                ? `${Math.round((totalCommission / totalFare) * 100)}% of fare`
                : '—'}
            </div>
          </div>

          {/* Withdrawals */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-500 text-xs uppercase tracking-wide">WITHDRAWALS</span>
            </div>
            <div className="font-medium text-gray-900">
              {formatNaira(totalWithdrawal)}
            </div>
            {analytics.withdrawals?.barsError ? (
              <div className="text-xs text-red-600 mt-1 max-w-[140px]">
                {"Couldn't load the monthly withdrawals, so the green bars are missing."}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentAndEarningCard;